import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Tooltip,
  Popover,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import { ReactComponent as ReloadIcon } from "../../assets/reload.svg";
import DateTimeIcon from "../../assets/fluent-mdl2_date-time.svg";

import { STATUS_META, TIME_RANGES, AUTO_REFRESH_OPTIONS } from "./PlatformHealthUtils";
import { HealthStatus } from "./types";
import { METRIC_ORDER } from "./Constants";
import DateTimeRangePicker from "../Dropdown/DateTimeRangeFilter/DateTimeRangePicker";
import SearchField from "../Common/SearchField";

import moment from "moment-timezone";

type MetricKey = Exclude<HealthStatus, "HEALTHY">;

interface PlatformHealthMetricsPortalProps {
  aggregate: Record<MetricKey, number>;
  activeStatusFilter: MetricKey | null;
  toggleMetric: (k: MetricKey | null) => void;
  openTimeMenu: (e: React.MouseEvent<HTMLElement>) => void;
  timeMenuAnchorEl: HTMLElement | null;
  timeMenuOpen: boolean;
  closeTimeMenu: () => void;
  timeRange: string;
  customStartDate: Date | null;
  customEndDate: Date | null;
  selectTimeRange: (range: string, start?: Date, end?: Date) => void;
  autoRefreshInterval: number;
  onAutoRefreshChange: (minutes: number) => void;
  searchDraft: string;
  setSearchDraft: (val: string) => void;
  handleSearchGo: () => void;
  handleSearchClear: () => void;
  searchText: string;
  startDateFromBackend?: string;
  availableDates?: string[];
}

type MetricLayout = {
  isNarrow: boolean;
  forceCompact: boolean;
  isTablet: boolean;
  isLaptop: boolean;
};

function tryFindPortalTarget(
  targetId: string,
  attempt: number,
  onFound: (el: HTMLElement) => void
): void {
  const el = document.getElementById(targetId);
  if (el) {
    onFound(el);
  } else if (attempt < 50) {
    setTimeout(() => tryFindPortalTarget(targetId, attempt + 1, onFound), 50);
  }
}

function getPlatformAffectedLabel(count: number, isTablet: boolean, isLaptop: boolean): string {
  if (isTablet || isLaptop) return `${count} PLTF\nAFFECTED`;
  return `${count} ${count > 1 ? "PLATFORMS" : "PLATFORM"}\nAFFECTED`;
}

function getTruncatedLabel(meta: any, isTablet: boolean, isLaptop: boolean): string {
  if (isTablet) {
    return meta.label.length > 9 ? meta.label.substring(0, 8) + "…" : meta.label;
  }
  if (isLaptop) {
    return meta.label.length > 7 ? meta.label.substring(0, 6) + "…" : meta.label;
  }
  return meta.label;
}

function getMetricContent(
  k: MetricKey,
  meta: any,
  count: number,
  active: boolean,
  isHovered: boolean,
  layout: MetricLayout
): React.ReactElement {
  const { isTablet, isLaptop } = layout;
  const textColor = active ? meta.color : "#fff";
  const baseTextSx = {
    fontWeight: 600,
    color: textColor,
    pointerEvents: "none" as const,
    lineHeight: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  };

  if (!isHovered) {
    return (
      <Typography sx={{ ...baseTextSx, fontSize: "14px", textAlign: "center" }}>
        {count}
      </Typography>
    );
  }

  if (k === "PLATFORM_AFFECTED") {
    return (
      <Typography
        sx={{
          ...baseTextSx,
          fontSize: isTablet || isLaptop ? "9.5px" : "10.75px",
          whiteSpace: "pre-line",
          textAlign: "center",
        }}
      >
        {getPlatformAffectedLabel(count, isTablet, isLaptop)}
      </Typography>
    );
  }

  const label = getTruncatedLabel(meta, isTablet, isLaptop);

  return (
    <Typography noWrap sx={{ ...baseTextSx, fontSize: isTablet || isLaptop ? "11.5px" : "13px" }}>
      {count} {label}
    </Typography>
  );
}



type AutoRefreshMenuItemProps = {
  opt: { value: number; label: string };
  selected: boolean;
  onAutoRefreshChange: (minutes: number) => void;
};

function AutoRefreshMenuItemRow({ opt, selected, onAutoRefreshChange }: Readonly<AutoRefreshMenuItemProps>) {
  return (
    <MenuItem
      key={opt.value}
      onClick={() => onAutoRefreshChange(opt.value)}
      sx={{
        backgroundColor: selected
          ? "rgba(25, 118, 210, 0.08) !important"
          : "#fff !important",
        color: "#000 !important",
        fontSize: "15px",
        fontWeight: 500,
        fontStyle: opt.value === 0 ? "italic" : "normal",
        py: 1.5,
        px: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        "& .MuiTypography-root": { color: "#000 !important" },
        "&:hover": { backgroundColor: "rgba(0,0,0,0.04) !important" },
      }}
    >
      <Typography
        sx={{
          color: "#1a1a1a !important",
          fontWeight: 500,
          fontSize: "15px",
          fontStyle: opt.value === 0 ? "italic" : "normal",
          fontFamily: "Montserrat, sans-serif",
        }}
      >
        {opt.label}
      </Typography>
      {selected && <CheckIcon sx={{ color: "#1976d2", fontSize: "22px", ml: 2 }} />}
    </MenuItem>
  );
}

function getExpandedBadgeMinWidth(isTablet: boolean, isLaptop: boolean): number | "auto" {
  if (isTablet) return 44;
  if (isLaptop) return 52;
  return "auto";
}

function getTimeRangeLabel(
  timeRange: string,
  customStartDate: Date | null,
  customEndDate: Date | null
): string {
  if (timeRange === "custom" && customStartDate && customEndDate) {
    return `${moment(customStartDate).tz("America/Chicago").format("MM-DD-YYYY HH:mm:ss")} to ${moment(customEndDate).tz("America/Chicago").format("MM-DD-YYYY HH:mm:ss")}`;
  }
  return TIME_RANGES.find((r) => r.value === timeRange)?.label || timeRange;
}

function getAutoRefreshTooltipTitle(isNarrow: boolean, autoRefreshInterval: number): string {
  if (isNarrow) {
    return AUTO_REFRESH_OPTIONS.find((t) => t.value === autoRefreshInterval)?.label || "No Auto Refresh";
  }
  return "Auto Refresh Settings";
}

type MetricBadgeProps = {
  metricKey: MetricKey;
  count: number;
  active: boolean;
  hoveredMetric: MetricKey | null;
  setHoveredMetric: (k: MetricKey | null) => void;
  toggleMetric: (k: MetricKey | null) => void;
  badgeHeight: number;
  layout: MetricLayout & { forceCompact: boolean };
};

function MetricBadge({
  metricKey,
  count,
  active,
  hoveredMetric,
  setHoveredMetric,
  toggleMetric,
  badgeHeight,
  layout,
}: Readonly<MetricBadgeProps>) {
  const { isNarrow, forceCompact, isTablet, isLaptop } = layout;
  const meta = STATUS_META[metricKey];
  const isHovered = hoveredMetric === metricKey;

  return (
    <Tooltip
      title={isNarrow || isTablet || isLaptop || forceCompact ? `${count} ${meta.label}` : ""}
      placement="bottom"
      arrow
    >
      <Box
        onMouseEnter={() => setHoveredMetric(metricKey)}
        onMouseLeave={() => setHoveredMetric(null)}
        onClick={() => (count > 0 ? toggleMetric(metricKey) : null)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: count === 0 ? "default" : "pointer",
          userSelect: "none",
          backgroundColor: active ? "#fff" : meta.color,
          opacity: count === 0 ? 0.45 : 1,
          borderBottom: active ? `3px solid ${meta.color}` : "none",
          overflow: "hidden",
          boxSizing: "border-box",
          px: isHovered ? 0.65 : 0.5,
          minWidth: isHovered ? getExpandedBadgeMinWidth(isTablet, isLaptop) : 38,
          flexShrink: 1,
          height: badgeHeight,
          transition: "min-width 0.2s ease, padding 0.2s ease",
        }}
      >
        {getMetricContent(metricKey, meta, count, active, isHovered, { isNarrow, forceCompact, isTablet, isLaptop })}
      </Box>
    </Tooltip>
  );
}

const PlatformHealthMetricsPortal = ({
  aggregate,
  activeStatusFilter,
  toggleMetric,
  openTimeMenu,
  timeMenuAnchorEl,
  timeMenuOpen,
  closeTimeMenu,
  timeRange,
  customStartDate,
  customEndDate,
  selectTimeRange,
  autoRefreshInterval,
  onAutoRefreshChange,
  searchDraft,
  setSearchDraft,
  handleSearchGo,
  handleSearchClear,
  searchText,
  startDateFromBackend,
  availableDates,
}: Readonly<PlatformHealthMetricsPortalProps>) => {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [hoveredMetric, setHoveredMetric] = useState<MetricKey | null>(null);
  const [localIsSearching, setLocalIsSearching] = useState(false);
  const [localIsClearingIcon, setLocalIsClearingIcon] = useState(false);

  const [customRangeAnchorEl, setCustomRangeAnchorEl] = useState<null | HTMLElement>(null);
  const customRangeOpen = Boolean(customRangeAnchorEl);

  const openCustomRange = (e: React.MouseEvent<HTMLElement>) => setCustomRangeAnchorEl(e.currentTarget);
  const closeCustomRange = () => setCustomRangeAnchorEl(null);

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isNarrow = useMediaQuery("(max-width:799px)");
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const handleSearchGoWithLoading = () => {
    setLocalIsSearching(true);
    handleSearchGo();
    setTimeout(() => setLocalIsSearching(false), 500);
  };

  const handleSearchClearWithLoading = () => {
    setLocalIsClearingIcon(true);
    handleSearchClear();
    setTimeout(() => setLocalIsClearingIcon(false), 500);
  };

  useEffect(() => {
    tryFindPortalTarget("platform-health-metrics-portal", 0, setPortalTarget);
  }, []);

  if (!portalTarget) return null;

  const forceCompact = searchText.trim().length > 0;

  const badgeHeight = isMobile ? 28 : 34;
  const iconBtnSize = isMobile ? "22px" : "26px";
  const iconFontSize = isMobile ? "0.875rem" : "1rem";

  const portalContent = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: isNarrow ? 0.4 : 0.75,
        flexWrap: "nowrap",
        py: 0.5,
        minWidth: 0,
        justifyContent: "flex-end",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isNarrow ? 0.4 : 1,
          flexShrink: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <Typography
          onClick={() => toggleMetric(null as any)}
          sx={{
            color: "#1976d2",
            fontWeight: 700,
            fontSize: isMobile ? "11px" : "12px",
            cursor: "pointer",
            fontFamily: "Montserrat",
            whiteSpace: "nowrap",
            flexShrink: 0,
            display: isNarrow ? "none" : "block",
            "&:hover": { textDecoration: "underline" },
            opacity: activeStatusFilter === null ? 0.5 : 1,
          }}
        >
          Show All
        </Typography>

        <Box
          sx={{
            width: "1px",
            height: "18px",
            backgroundColor: "rgba(0,0,0,0.15)",
            mx: 0.25,
            flexShrink: 0,
            display: isNarrow || forceCompact ? "none" : "block",
          }}
        />

        <Box
          sx={{
            borderRadius: "0px",
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.10)",
            height: badgeHeight,
            display: "flex",
            gap: "1px",
            background: "#fff",
            flexShrink: 1,
            minWidth: 0,
            maxWidth: "100%",
            overflowX: "hidden",
            overflowY: "visible",
          }}
        >
          {(METRIC_ORDER as readonly MetricKey[]).map((k) => (
            <MetricBadge
              key={k}
              metricKey={k}
              count={aggregate[k] ?? 0}
              active={activeStatusFilter === k}
              hoveredMetric={hoveredMetric}
              setHoveredMetric={setHoveredMetric}
              toggleMetric={toggleMetric}
              badgeHeight={badgeHeight}
              layout={{ isNarrow, forceCompact, isTablet, isLaptop }}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 0,
          flexGrow: 0,
          minWidth: 0,
        }}
      >
        <SearchField
          value={searchDraft}
          type="health"
          onChange={setSearchDraft}
          onSearch={handleSearchGoWithLoading}
          onClear={handleSearchClearWithLoading}
          searchIconSize={24}
          otherIconsSize={18}
          isSearching={localIsSearching}
          isClearingIcon={localIsClearingIcon}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {autoRefreshInterval > 0 && (
            <Typography
              sx={{
                color: "#48B5ED",
                fontSize: isTablet ? "11px" : "12px",
                fontWeight: 500,
                fontFamily: "Montserrat",
                whiteSpace: "nowrap",
                // KEY FIX: hide "Every X Mins" label on narrow/mobile to reclaim space
                // The tooltip on the reload icon already shows the interval on narrow screens
                display: isNarrow ? "none" : "block",
              }}
            >
              Every {AUTO_REFRESH_OPTIONS.find((opt) => opt.value === autoRefreshInterval)?.label}
            </Typography>
          )}

          <Tooltip
            title={getAutoRefreshTooltipTitle(isNarrow, autoRefreshInterval)}
            placement="bottom"
          >
            <IconButton
              size="small"
              onClick={openTimeMenu}
              sx={{
                border: "none",
                borderRadius: "3px",
                backgroundColor: "transparent",
                flexShrink: 0,
                width: iconBtnSize,
                height: iconBtnSize,
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
              aria-label="Auto Refresh Interval"
            >
              <ReloadIcon style={{ width: iconFontSize, height: iconFontSize, color: "#48B5ED" }} />
            </IconButton>
          </Tooltip>

          <Typography
            sx={{
              color: "#48B5ED",
              fontSize: isTablet ? "11px" : "12px",
              fontWeight: 500,
              fontFamily: "Montserrat",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: isNarrow ? "70px" : "none",
            }}
          >
            {getTimeRangeLabel(timeRange, customStartDate, customEndDate)}
          </Typography>
        </Box>

        <Tooltip title="Select Date Range" placement="bottom" arrow>
          <IconButton
            size="small"
            onClick={openCustomRange}
            sx={{
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: "3px",
              backgroundColor: "#fff",
              flexShrink: 0,
              width: iconBtnSize,
              height: iconBtnSize,
              ml: 0.25,
            }}
            aria-label="Open Calendar"
          >
            <img src={DateTimeIcon} alt="date-time" style={{ width: "16px", height: "16px" }} />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={timeMenuAnchorEl}
          open={timeMenuOpen}
          onClose={closeTimeMenu}
          container={document.body}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "#fff !important",
                color: "#000 !important",
                border: "1px solid rgba(0,0,0,0.10) !important",
                borderRadius: "12px !important",
                mt: 0.5,
                minWidth: 240,
                boxShadow: "0px 4px 18px rgba(0,0,0,0.12) !important",
                py: 1,
              },
            },
          }}
          MenuListProps={{ sx: { py: 0, backgroundColor: "#fff !important" } }}
        >
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "16px",
                color: "#1a1a1a",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              Auto Refresh In
            </Typography>
          </Box>

          {(AUTO_REFRESH_OPTIONS ?? []).map((opt) => (
            <AutoRefreshMenuItemRow
              key={opt.value}
              opt={opt}
              selected={autoRefreshInterval === opt.value}
              onAutoRefreshChange={onAutoRefreshChange}
            />
          ))}
        </Menu>

        <Popover
          open={customRangeOpen}
          anchorEl={customRangeAnchorEl}
          onClose={closeCustomRange}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: "8px",
                boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
                border: "1px solid #E0E0E0",
              },
            },
          }}
        >
          <DateTimeRangePicker
            onClose={closeCustomRange}
            onApply={selectTimeRange}
            initialRange={timeRange}
            initialStartDate={customStartDate}
            initialEndDate={customEndDate}
            timezone="CST"
            startDateFromBackend={startDateFromBackend}
            availableDates={availableDates}
          />
        </Popover>
      </Box>
    </Box>
  );

  return createPortal(portalContent, portalTarget);
};

export default PlatformHealthMetricsPortal;
